import React, { useEffect, useState } from "react";
import { fetchAvailalbeAppointmentsForAutomaticResponseById } from "./DataServices/AppointmentsDataServices";
import {
  fetchAppointmentResponse,
  fetchAutomaticResponseHistoryById,
} from "./DataServices/AutomaticResponsesDataServices";
import { useNavigate, useParams } from "react-router-dom";
import { IAutomaticResponseHistory } from "./Models";
import Notification from "./Notification";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { CloseOutlined, PermIdentityOutlined } from "@mui/icons-material";
import { GetMemberAndGuarntor, RemoveMemberAndGuarntor } from "./DataServices/ProspectiveTenantDataService";
import MessageBox from "./MessageBox";
import Loading from "./Loading";

interface Slot {
  id: string;
  listingID: string;
  startTime: string;
  endTime: string;
  capacity: number;
  slotOrder: number;
  isDeleted: boolean;
}

export default function AppointmentScheduleAndConfirmation({ type }: { type: string }) {
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [notificationType, setNotificationType] = useState("success");
  const [toDeleteMember, setToDeleteMember] = useState("")
  const [slots, setSlots] = useState<Slot[]>([]);
  const { id } = useParams<{ id: string | undefined }>();
  const [selectedAppointment, setSelectedAppointment] = useState('');
  const [loading, setLoading] = useState(false);
  const [automaticResponseHistory, setAutomaticResponseHistory] = useState<IAutomaticResponseHistory>();
  const [selectedSlots, setSelectedSlots] = useState();
  const [phone, setPhone] = useState<string>();
  const [isReschedule, setIsReschedule] = useState<Boolean>(type === "reschedule");
  const [memberGuarantorInfos, setMemberGuarantorInfos] = useState<{ id: string; name: string; isGuarantor: boolean; }[]>([]);
  const [memberInput, setMemberInput] = useState<string>("");
  const [guarantorInput, setGuarantorInput] = useState<string>("");
  const navigate = useNavigate();

  const okButtonFunction = async () => {
    setLoading(true)
    try {
      if (id && selectedAppointment) {
        setLoading(true)
        await fetchAppointmentResponse(id, true, selectedAppointment, phone, memberGuarantorInfos);
        setIsReschedule(true);
        setConfirmationMessage("Thanks for your confirmation");
        setLoading(false);
      }
    } catch (error) {
      setNotificationMessage(
        "Confirmation failed. Please try again or contact support."
      );
      setIsNotificationOpen(true);
      setLoading(false);
      setTimeout(() => {
        setIsNotificationOpen(false);
      }, 3000);
      console.error("Error fetching appointment response:", error);
    }
    setLoading(false)
    if (automaticResponseHistory?.moveInDate) navigate(`/nonvacant-confirmation/${id}`)
    else navigate(`/vacant-confirmation/${id}`)
  };

  useEffect(() => {
    if (id) {
      fetchAvailalbeAppointmentsForAutomaticResponseById(id)
        .then((fetchedSlots) => {
          setSlots(fetchedSlots);
        })
        .catch((error) => {
          console.error("Error fetching confirmed appointments:", error);
        });
    }
    // Assume fetchConfirmedAppointments() returns a Promise
  }, []); // Empty dependency array means this effect runs once when the component mounts

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const response = await fetchAutomaticResponseHistoryById(id);
          GetMemberAndGuarntor(id).then(data => {
            setMemberGuarantorInfos(data)
          }).catch(() => {
            throw Error;
          })
          setAutomaticResponseHistory(response);
          setPhone(response.phone)
          if (!response.moveInDate) {
            setSelectedAppointment(response.suggestedAppointmentID)
          }
          if (response.confirmed && !isReschedule) setConfirmationMessage("Appointment already confirmed!")
          if ((response.rejected || response.cancelled) && !isReschedule) {
            navigate(`/cancel-confirmation/${id}`)
          }
        }
      } catch (error) {
        console.error("Error fetching:", error);
      }
    };

    fetchData();
  }, [id, confirmationMessage, isReschedule]);


  useEffect(() => {
    if (slots.length > 0 && automaticResponseHistory?.moveInDate && !selectedAppointment) {
      // Check if it's rescheduleByAdmin first
      if (type === 'rescheduleByAdmin') {
        setSelectedAppointment(automaticResponseHistory.suggestedAppointmentID);
      } else {
        // For other moveInDate scenarios, select the first available slot
        setSelectedAppointment(slots[0].id);
      }
    }
  }, [slots, automaticResponseHistory?.moveInDate, selectedAppointment, type, automaticResponseHistory?.suggestedAppointmentID]);



  // useEffect(() => {
  //   if (slots.length > 0 && selectedAppointment?.length === 0)
  //     setSelectedAppointment(slots[0].id)
  // }, [slots,selectedAppointment])

  const cancelButtonFunction = async () => {
    // Perform any logic needed on "No" button click
    setLoading(true)
    try {
      if (id) {
        await fetchAppointmentResponse(id, false);
        setConfirmationMessage(
          "No worries! We'll keep you informed about upcoming schedules."
        );

        // Handle the case when the response is false
      }
    } catch (error) {
      setIsNotificationOpen(true);
      setNotificationMessage(
        "Rejection failed. Please try again or contact support."
      );
      setTimeout(() => {
        setIsNotificationOpen(false);
      }, 3000);
      console.error("Error fetching appointment response:", error);
    } finally {
      setLoading(false)
      navigate(`/cancel-confirmation/${id}`)
    }
  };

  const addTenantGuarantorInfos = (name: string, isGuarantor: boolean) => {
    setMemberGuarantorInfos([...memberGuarantorInfos, { id: (memberGuarantorInfos.length + 1).toString(), name: name, isGuarantor: isGuarantor }])
    setMemberInput("");
    setGuarantorInput("");
  }
  const removeMemberGuarantorInfos = async (id: string) => {
    setLoading(true)
    if (id.includes('-')) {
      await RemoveMemberAndGuarntor(id)
    }
    setMemberGuarantorInfos(memberGuarantorInfos.filter(e => e.id != id))
    setOpenMessageBox(false);
    setLoading(false)
  }

  const isPhoneVaild = () => {
    if (phone && phone.trim().length > 6) return !/[a-zA-Z]/.test(phone.trim());
    return false;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <header className="bg-blue-700 text-white py-4 px-6 shadow-xl">
        <h1 className="text-xl font-semibold">Confirm Your Appointment</h1>
        <p className="text-sm text-gray-100">Please confirm or reschedule your visit</p>
      </header>
      <main className="px-4 py-8 md:px-16 lg:px-32 space-y-10">
        {confirmationMessage && (
          <div className="h-full flex items-center justify-center p-6">

            <div className="bg-white rounded-2xl shadow-md max-w-xl w-full p-8 text-gray-800">

              <div className="text-center mb-6">
                <h1 className="text-xl font-extrabold text-indigo-700">Appointment Status</h1>
                <p className="text-sm text-gray-500">Here are the details of your appointment</p>
              </div>

              <div className="flex justify-center mb-6">
                <svg width="120" height="120" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <rect x="10" y="30" width="80" height="60" rx="12" fill="#F5F5F5" stroke="#CCCCCC" stroke-width="2" />
                  <rect x="10" y="10" width="80" height="30" rx="8" fill="#6366F1" />
                  <circle cx="20" cy="25" r="6" fill="white" stroke="#888" stroke-width="2" />
                  <circle cx="80" cy="25" r="6" fill="white" stroke="#888" stroke-width="2" />
                  <text x="50" y="30" font-size="14" text-anchor="middle" fill="white" font-family="Arial" font-weight="bold">{automaticResponseHistory ? new Date(automaticResponseHistory.startTime).toLocaleString('en-US', { month: 'short' }) : "Jan"}</text>
                  <text x="50" y="75" font-size="35" text-anchor="middle" fill="black" font-family="Arial" font-weight="bold">{automaticResponseHistory ? new Date(automaticResponseHistory.startTime).getDate() : 0}</text>
                </svg>
              </div>

              <div className="text-center mb-4">
                <div className="text-green-600 text-lg font-semibold">
                  Hey {automaticResponseHistory?.firstName}, your appointment<br /> has been successfully confirmed
                </div>
              </div>
              <div className="text-center text-2xl font-bold text-gray-800 mb-2">{automaticResponseHistory?.title}</div>

              <div className="text-center text-indigo-600 text-sm mb-6">
                {automaticResponseHistory && formatDateTime(automaticResponseHistory.startTime.toString()) + " "}
                to {automaticResponseHistory && formatTime(automaticResponseHistory.endTime.toString())}
              </div>
              <div className="text-center text-gray-600 text-sm">
                <p className="mb-2">
                  Need to make changes?{" "}
                  {!automaticResponseHistory?.moveInDate && (
                    <>
                      <a
                        href={`/reschedule-confirmation/${id}`}
                        className="text-indigo-600 underline hover:text-indigo-800"
                      >
                        Click here to reschedule
                      </a>
                      {" or "}
                    </>
                  )}
                  <a
                    href={`/cancel-confirmation/${id}`}
                    className="text-red-600 underline hover:text-red-800"
                  >
                    cancel your appointment
                  </a>.
                </p>


                <p>We look forward to seeing you!</p>
              </div>
            </div>

          </div>
        )}
        {(!confirmationMessage &&
          <>
            <section className="bg-white p-6 rounded-md shadow-sm">
              <h2 className="text-xl font-semibold mb-4">{automaticResponseHistory?.title}</h2>
              <p className="text-gray-700">
                {slots.length > 0
                  ? "Please review and confirm your availability for a scheduled visit. This will help us ensure the apartment suits your needs and preferences before moving forward. If you're ready, kindly select a time slot that works best for you from the options below."
                  : "Unfortunately, there are currently no available time slots for a visit. Please check back later or contact us for assistance."}
              </p>

            </section>
            {slots.length > 0 && <section className="bg-white p-6 rounded-md shadow-sm">
              <h2 className="text-xl font-semibold mb-4">Confirm Attendance</h2>
              <p className="text-gray-700 mb-4">Please confirm your availability and enter your contact details.</p>

              <label className="block mb-2 text-sm font-medium text-gray-900">Select a time slot</label>
              <select
                className="block w-full border border-gray-300 rounded p-2 mb-4"
                value={selectedAppointment}
                onChange={(e) =>
                  setSelectedAppointment(e.target.value)
                }

                style={{ maxHeight: '400px', overflowY: 'auto' }}
              >
                {type === "rescheduleByAdmin" ?
                  automaticResponseHistory && <option value={selectedAppointment} className="bg-white text-black">
                    {formatDateTime(automaticResponseHistory.startTime.toString())}{" "}
                    -{" "}
                    {formatDateTime(automaticResponseHistory.endTime.toString())}
                  </option> : <>
                    {automaticResponseHistory?.moveInDate ? <option value={slots[0]?.id} className="bg-white text-black">
                      {formatDateTime(slots[0].startTime)}{" "}
                      -{" "}
                      {formatDateTime(slots[0].endTime)}
                    </option> : slots.sort((a, b) => {
                      const timeA = new Date(a.startTime).getTime() || 0;  // Fallback to 0 if invalid
                      const timeB = new Date(b.startTime).getTime() || 0;
                      return timeA - timeB;
                    }).map((slot, index) => (


                      <option className="bg-white text-black" value={slot.id} key={index} selected={selectedAppointment === slot.id}>
                        {formatDateTime(slot.startTime)}{" "}
                        -{" "}
                        {formatDateTime(slot.endTime)}
                      </option>
                    ))}</>}
              </select>

              <label className="block mb-2 text-sm font-medium text-gray-900">Phone number</label>
              <div className="relative mb-4">
                <div className="absolute inset-y-0 start-0 top-0 flex items-center ps-3.5 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 19 18">
                    <path d="M18 13.446a3.02 3.02 0 0 0-.946-1.985l-1.4-1.4a3.054 3.054 0 0 0-4.218 0l-.7.7a.983.983 0 0 1-1.39 0l-2.1-2.1a.983.983 0 0 1 0-1.389l.7-.7a2.98 2.98 0 0 0 0-4.217l-1.4-1.4a2.824 2.824 0 0 0-4.218 0c-3.619 3.619-3 8.229 1.752 12.979C6.785 16.639 9.45 18 11.912 18a7.175 7.175 0 0 0 5.139-2.325A2.9 2.9 0 0 0 18 13.446Z" />
                  </svg>
                </div>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="block w-full border border-gray-300 rounded p-2 mb-4 ps-10" pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" placeholder="123-456-7890" required />
              </div>

           


              <div className="flex flex-col mt-4 sm:flex-row gap-4">
                <button
                  className="bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300 rounded"
                  onClick={okButtonFunction}
                  disabled={!isPhoneVaild()}>
                  Yes, I will attend
                </button>
                <button
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-900 font-medium py-2 px-4 rounded"
                  onClick={cancelButtonFunction}>
                  No, I can't attend
                </button>
              </div>
            </section>}
          </>
        )}
      </main>
      {loading && <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
        <Loading />
      </div>}
      <Notification
        isOpen={isNotificationOpen}
        setIsOpen={setIsNotificationOpen}
        message={notificationMessage}
        messageType={notificationType}
      />
      <MessageBox
        title="Remove Member or Guarantor"
        message="Are you sure you want to delete the member or guarantor?"
        isOpen={openMessageBox}
        setIsOpen={setOpenMessageBox}
        okButtonText="Ok"
        cancelButtonText="Cancel"
        okButtonFunction={() => removeMemberGuarantorInfos(toDeleteMember)}
        cancelButtonFunction={() => setOpenMessageBox(false)}
        okButtonStyle={{
          backgroundColor: "rgb(79, 70, 229)",
        }}
        iconColor={{ color: "rgb(79, 70, 229)" }}
      />
    </div>
  );
}

export function formatDateTime(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'long',  // "May"
    day: 'numeric',  // "7"
    year: 'numeric',  // "2022"
    hour: 'numeric',  // "6"
    minute: '2-digit',  // "00"
    hour12: true,  // "PM/AM"
  });
};

export function formatTime(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}